/**
 * Helper class for building OGC filter.
 */

export const FilterHelper = {

  /**
   * Create a OGC filter for matching features based on a timestamp.
   *
   * @param {String} propertyName The property of the layer that contains the time information.
   * @param {Date} timeStampStart The timestamp with the start time to use for the filter.
   * @param {Date} timeStampEnd The timestamp with the end time to use for the filter.
   * @param {String} wfsVersion The version of the WFS. Either '1.1.0' or '2.0.0'
   * @returns {Array} An array with the upper and the lower time boundary.
   */
  createTimeFilter: function (propertyName, timeStampStart, timeStampEnd, wfsVersion) {
    if (!propertyName || !timeStampStart || !timeStampEnd || !wfsVersion) {
      return;
    }
    const isoTimeStringStart = timeStampStart.toISOString();
    const isoTimeStringEnd = timeStampEnd.toISOString();

    let prefix;
    let propertyTag;
    switch (wfsVersion) {
      case '1.1.0':
        prefix = 'ogc';
        propertyTag = 'PropertyName';
        break;
      case '2.0.0':
        prefix = 'fes';
        propertyTag = 'ValueReference';
        break;
      default:
        return;
    }

    const lowerBoundaryTpl = `
    <${prefix}:PropertyIsGreaterThanOrEqualTo>
      <${prefix}:${propertyTag}>
        ${propertyName}
      </${prefix}:${propertyTag}>
      <${prefix}:Literal>
        ${isoTimeStringStart}
      </${prefix}:Literal>
    </${prefix}:PropertyIsGreaterThanOrEqualTo>`;

    const upperBoundaryTpl = `
    <${prefix}:PropertyIsLessThanOrEqualTo>
      <${prefix}:${propertyTag}>
        ${propertyName}
      </${prefix}:${propertyTag}>
      <${prefix}:Literal>
        ${isoTimeStringEnd}
      </${prefix}:Literal>
    </${prefix}:PropertyIsLessThanOrEqualTo>`;

    return [lowerBoundaryTpl, upperBoundaryTpl];
  },

  /**
   * Get XML for filtering features by extent.
   *
   * @param {String} srsName The projection as String e.g. 'EPSG:3857'
   * @param {ol.extent.Extent} extent The extent of the area to filter.
   * @param {String} propertyName The name of the geometry property.
   * @param {String} wfsVersion The version of the WFS. Either '1.1.0' or '2.0.0'
   * @returns The respective OGC Filter XML.
   */
  createExtentFilter: function (srsName, extent, propertyName, wfsVersion) {
    if (!srsName || !extent || !propertyName || !wfsVersion) {
      return;
    }

    let filter;
    switch (wfsVersion) {
      // TODO: maybe we can also use Intersects here. This would yield less redundant code.
      case '1.1.0':
        filter = `
        <ogc:BBOX>
          <ogc:PropertyName>
            ${propertyName}
          </ogc:PropertyName>
          <gml:Envelope xmlns:gml="http://www.opengis.net/gml" srsName="${srsName}">
            <gml:lowerCorner>
              ${extent[0]} ${extent[1]}
            </gml:lowerCorner>
            <gml:upperCorner>
              ${extent[2]} ${extent[3]}
            </gml:upperCorner>
          </gml:Envelope>
        </ogc:BBOX>
            `;
        break;

      case '2.0.0':
        // TODO: solve more elegantly
        //      - either use 'extent' function and coordinate formatter or array.join(',')
        //      OR
        //      - convert extent to GML using formatter
        const xMin = extent[0];
        const yMin = extent[2];
        const xMax = extent[1];
        const yMax = extent[3];
        filter = `
        <fes:Intersects>
              <fes:ValueReference>
                ${propertyName}
              </fes:ValueReference>
              <gml:Polygon srsName="${srsName}">
                  <gml:exterior>
                      <gml:LinearRing>
                          <gml:coordinates>
                              ${xMin},${yMin}
                              ${xMax},${yMin}
                              ${xMax},${yMax}
                              ${xMin},${yMax}
                              ${xMin},${yMin}
                          </gml:coordinates>
                      </gml:LinearRing>
                  </gml:exterior>
              </gml:Polygon>
          </fes:Intersects>`;
        break;
      default:
        return;
    }
    return filter;
    ;
  },

  /**
   * Connect OGC filters with the  'And' operator.
   * @param {Array} filters The filters to connect.
   * @param {String} wfsVersion The version of the WFS. Either '1.1.0' or '2.0.0'
   * @returns {String} The connected filter as XML.
   */
  connectFiltersWithAnd: function (filters, wfsVersion) {
    if (!filters || !Array.isArray(filters) || filters.length < 2 || !wfsVersion) {
      return;
    }

    let start;
    let end;
    switch (wfsVersion) {
      case '1.1.0':
        start = `
        <ogc:Filter xmlns:ogc="http://www.opengis.net/ogc">
           <ogc:And>`;
        end = `
           </ogc:And>
        </ogc:Filter>`;
        break;
      case '2.0.0':
        start = `
        <fes:Filter
           xsi:schemaLocation="http://www.opengis.net/fes/2.0
                               http://schemas.opengis.net/filter/2.0/filterAll.xsd
                               http://www.opengis.net/gml/3.2
                               http://schemas.opengis.net/gml/3.2.1/gml.xsd"
          xmlns:fes="http://www.opengis.net/fes/2.0"
          xmlns:gml="http://www.opengis.net/gml/3.2"
          xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">
          <fes:And>
        `;
        end = `
          </fes:And>
        </fes:Filter>
        `;
        break;
      default:
        return;
    }

    let connectedFilter = start;
    filters.forEach((filter) => {
      if (!filter) {
        return;
      }
      connectedFilter += filter
    });
    connectedFilter += end;
    return connectedFilter;
  }
};



// WEBPACK FOOTER //
// ./app/util/OgcFilter.js